import { useEffect, useRef, useState } from 'react';

type Position = { x: number; y: number };
type Size = { width: number; height: number };

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

type UseDragResizeParams = {
  initialPosition: Position;
  initialSize: Size;
  minSize: Size;
  /** ドラッグ/リサイズ完了時のコールバック */
  onChangeEnd?: (position: Position, size: Size) => void;
};

const CURSOR_MAP: Record<ResizeDirection, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

/**
 * ドラッグ移動 + 8方向リサイズのカスタムフック
 * SidePanel.tsx のマウスイベントパターンに準拠
 */
export const useDragResize = ({
  initialPosition,
  initialSize,
  minSize,
  onChangeEnd,
}: UseDragResizeParams) => {
  const [position, setPosition] = useState<Position>(initialPosition);
  const [size, setSize] = useState<Size>(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  // コールバックを安定参照で保持（effect依存配列に含めない）
  const onChangeEndRef = useRef(onChangeEnd);
  onChangeEndRef.current = onChangeEnd;

  // ドラッグ/リサイズ開始時の基準値を保持
  const dragStartRef = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });
  const resizeStartRef = useRef({
    mouseX: 0,
    mouseY: 0,
    width: 0,
    height: 0,
    posX: 0,
    posY: 0,
    direction: 'se' as ResizeDirection,
  });
  // mouseup 時に最新の position/size を参照するための ref
  const positionRef = useRef(position);
  const sizeRef = useRef(size);
  positionRef.current = position;
  sizeRef.current = size;

  // ドラッグ開始
  const handleDragStart = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    if ((e.target as HTMLElement).closest('textarea')) return;

    setIsDragging(true);
    dragStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      posX: position.x,
      posY: position.y,
    };
    document.body.style.cursor = 'move';
    document.body.style.userSelect = 'none';
  };

  // リサイズ開始
  const handleResizeStart = (e: React.MouseEvent, direction: ResizeDirection) => {
    e.stopPropagation();
    setIsResizing(true);
    resizeStartRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      width: size.width,
      height: size.height,
      posX: position.x,
      posY: position.y,
      direction,
    };
    document.body.style.cursor = CURSOR_MAP[direction];
    document.body.style.userSelect = 'none';
  };

  // ドラッグ中の移動処理
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { mouseX, mouseY, posX, posY } = dragStartRef.current;
      const dx = e.clientX - mouseX;
      const dy = e.clientY - mouseY;

      const newX = Math.max(0, Math.min(window.innerWidth - sizeRef.current.width, posX + dx));
      const newY = Math.max(0, Math.min(window.innerHeight - sizeRef.current.height, posY + dy));

      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      onChangeEndRef.current?.(positionRef.current, sizeRef.current);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // リサイズ中の処理
  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const { mouseX, mouseY, width, height, posX, posY, direction } = resizeStartRef.current;
      const dx = e.clientX - mouseX;
      const dy = e.clientY - mouseY;

      let newWidth = width;
      let newHeight = height;
      let newX = posX;
      let newY = posY;

      if (direction.includes('e')) {
        newWidth = Math.max(minSize.width, width + dx);
      }
      if (direction.includes('w')) {
        const proposedWidth = Math.max(minSize.width, width - dx);
        newX = posX + (width - proposedWidth);
        newWidth = proposedWidth;
      }

      if (direction.includes('s')) {
        newHeight = Math.max(minSize.height, height + dy);
      }
      if (direction === 'n' || direction === 'ne' || direction === 'nw') {
        const proposedHeight = Math.max(minSize.height, height - dy);
        newY = posY + (height - proposedHeight);
        newHeight = proposedHeight;
      }

      setSize({ width: newWidth, height: newHeight });
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      onChangeEndRef.current?.(positionRef.current, sizeRef.current);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, minSize.width, minSize.height]);

  // アンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, []);

  return {
    position,
    size,
    isDragging,
    isResizing,
    isInteracting: isDragging || isResizing,
    handleDragStart,
    handleResizeStart,
  };
};
